import React, { useEffect, useRef, useState } from "react";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import useExcel from "../hooks/useExcel";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { LoadingContainer, HttpRequestStatus } from "./loadingContainer";
import ExcelImportSummaryDialog from "../dialogs/excelImportSummaryDialog";
import { UploadProductsResponse } from "../api/importProducts.api";
import { ExcelUploadResponse } from "../api/generated/schemas";

type CustomFileType = {
  name: string;
  size: number;
  type: string;
  objectURL: string;
  isParsing: boolean;
  file: any;
};

const FileUploadContainer = styled.div`
  max-width: 700px;
  min-height: 300px;

  .p-fileupload .p-fileupload-content {
    background-color: var(--gray-50);
    color: var(--text-color);
    border: none;
    border-radius: 0.25rem;
  }
`;

const AcceptedFileFormsText = styled.span`
  color: var(--text-color-secondary);
  font-size: 0.8rem;
  display: block;
  margin-top: 10px;
`;

interface FileUploadFieldProps {
  existingReportId?: number;
  postProductExcel?: (
    file: File,
    existingReportId?: number,
  ) => Promise<UploadProductsResponse | void>;
  onHide?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onImportComplete?: () => void;
  propYear?: number;
  propQuarter?: number;
  propMonth?: number;
}

interface ImportSummary {
  createdCount?: number;
  updatedCount?: number;
  errors?: string[];
  warnings?: string[];
  navigateToReportId?: number | null;
}

export default function FileUploadField({
  existingReportId,
  postProductExcel,
  onHide,
  onLoadingChange,
  onImportComplete,
  propYear,
  propQuarter,
  propMonth,
}: FileUploadFieldProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentFiles, setCurrentFiles] = useState<CustomFileType[]>([]);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(
    null,
  );
  const { postExcel } = useExcel();
  const fileUploadRef = useRef<FileUpload>(null);

  const isUploading = currentFiles.some((f) => f.isParsing);

  useEffect(() => {
    onLoadingChange?.(isUploading);
  }, [isUploading]);

  const [year, quarter, month] = useUIStore(
    useShallow((state: UIState) => [
      state.selectedYearForNewReport,
      state.selectedQuarterForNewReport,
      state.selectedMonthForNewReport,
    ]),
  );

  const onSelect = (e) => {
    const newFiles: CustomFileType[] = e.files.map((file) => {
      return {
        name: file.name,
        size: file.size,
        type: file.type,
        objectURL: file.objectURL,
        isParsing: true,
        file: file,
      };
    });

    // Update the current files state
    setCurrentFiles((prev) => [...prev, ...newFiles]);

    // Clear the file upload component
    fileUploadRef.current?.clear();

    // Parse the uploaded files
    newFiles.forEach(async (file) => {
      if (postProductExcel) {
        if (file) {
          postProductExcel(file.file, existingReportId)
            .then((response: UploadProductsResponse | void) => {
              if (response) {
                setImportSummary({
                  createdCount: response.createdProducts,
                  errors: response.errors,
                  warnings: response.warnings,
                });
              }
              file.isParsing = false;
              // update the file state to indicate parsing is complete
              setCurrentFiles((prev) =>
                prev.map((f) => (f.name === file.name ? file : f)),
              );
            })
            .catch((error) => {
              console.error("Failed to parse Excel file:", error);
              file.isParsing = false;
              // update the file state to indicate parsing is complete
              setCurrentFiles((prev) =>
                prev.map((f) => (f.name === file.name ? file : f)),
              );
            });
        }
      } else {
        if (file) {
          try {
            const result: ExcelUploadResponse | undefined = await postExcel(
              file.file,
              existingReportId,
              year || propYear,
              quarter || propQuarter,
              month || propMonth,
            );

            if (result) {
              setImportSummary({
                createdCount: result.createdReportRows,
                updatedCount: result.updatedReportRows,
                errors: result.errors ?? undefined,
                warnings: result.warnings ?? undefined,
                navigateToReportId: !existingReportId
                  ? result.reportId
                  : undefined,
              });
            }

            file.isParsing = false;
            // update the file state to indicate parsing is complete
            setCurrentFiles((prev) =>
              prev.map((f) => (f.name === file.name ? file : f)),
            );
          } catch (error) {
            console.error("Failed to parse Excel file:", error);
            file.isParsing = false;
            // update the file state to indicate parsing is complete
            setCurrentFiles((prev) =>
              prev.map((f) => (f.name === file.name ? file : f)),
            );
          }
        }
      }
    });
  };

  const handleSummaryDialogHide = () => {
    const navigateToReportId = importSummary?.navigateToReportId;

    setImportSummary(null);

    existingReportId &&
      queryClient.invalidateQueries({
        queryKey: ["reports", existingReportId],
      });

    onImportComplete && onImportComplete();

    if (navigateToReportId) {
      navigate(`/report/${navigateToReportId}`);
    }

    onHide && onHide();
  };

  const headerTemplate = (options) => {
    const { className, chooseButton, uploadButton, cancelButton } = options;

    return (
      <div
        className={className}
        style={{
          backgroundColor: "transparent",
          visibility: "hidden",
          alignItems: "center",
        }}
      >
        {chooseButton}
        {uploadButton}
        {cancelButton}
        <div className="flex align-items-center gap-3 ml-auto"></div>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px",
          backgroundColor: "var(--gray-50)",
        }}
      >
        <i
          className="pi pi-upload"
          style={{
            fontSize: "2em",
            borderRadius: "50%",
            backgroundColor: "var(--gray-50)",
            color: "var(--surface-d)",
            margin: "1rem auto",
          }}
        ></i>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            margin: "20px 0",
            width: "100%",
          }}
        >
          <span
            style={{ fontSize: "1.2em", color: "var(--text-color)" }}
            className="my-5"
          >
            {t("fileUpload.empty")}
          </span>
          <span style={{ fontSize: "1.2em", color: "var(--text-color)" }}>
            {t("label-or")}
          </span>
          <Button
            style={{ width: "100%" }}
            label={t("fileUpload.choose")}
            onClick={handleChooseClick}
          />
        </div>
      </div>
    );
  };

  const chooseOptions = {
    icon: "pi pi-fw pi-images",
    iconOnly: true,
    className: "custom-choose-btn p-button-rounded p-button-outlined",
  };
  const uploadOptions = {
    icon: "pi pi-fw pi-cloud-upload",
    iconOnly: true,
    className:
      "custom-upload-btn p-button-success p-button-rounded p-button-outlined",
  };
  const cancelOptions = {
    icon: "pi pi-fw pi-times",
    iconOnly: true,
    className:
      "custom-cancel-btn p-button-danger p-button-rounded p-button-outlined",
  };

  const handleChooseClick = () => {
    if (fileUploadRef.current) {
      const fileInput = fileUploadRef.current.getInput();
      if (fileInput) {
        fileInput.click();
      }
    }
  };

  return (
    <FileUploadContainer>
      <LoadingContainer
        status={
          isUploading ? HttpRequestStatus.LOADING : HttpRequestStatus.IDLE
        }
      >
        <Tooltip
          target=".custom-choose-btn"
          content={t("label-choose")}
          position="bottom"
        />
        <Tooltip
          target=".custom-upload-btn"
          content="Upload"
          position="bottom"
        />
        <Tooltip
          target=".custom-cancel-btn"
          content="Clear"
          position="bottom"
        />

        <FileUpload
          ref={fileUploadRef}
          name="demo[]"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          maxFileSize={1000000}
          onSelect={onSelect}
          headerTemplate={headerTemplate}
          emptyTemplate={emptyTemplate}
          chooseOptions={chooseOptions}
          uploadOptions={uploadOptions}
          cancelOptions={cancelOptions}
        />
        <AcceptedFileFormsText>
          {t("text-accepted-file-formats")}
        </AcceptedFileFormsText>
      </LoadingContainer>
      <ExcelImportSummaryDialog
        show={!!importSummary}
        onHide={handleSummaryDialogHide}
        createdCount={importSummary?.createdCount}
        updatedCount={importSummary?.updatedCount}
        errors={importSummary?.errors}
        warnings={importSummary?.warnings}
      />
    </FileUploadContainer>
  );
}
